import React, { useState, useEffect } from "react";
import {
  Layout,
  Table,
  Tag,
  Button,
  Typography,
  Tooltip,
  Row,
  Col,
  Space,
  Spin,
} from "antd";
import { FileTextOutlined, FilePdfOutlined, EyeOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import { motion } from "framer-motion";

import {
  getInvoicesByCustomer,
  downloadInvoicePdf,
  getInvoiceById,
} from "../../../services/invoiceService";
import { getInvoiceStatusInfo } from "../../../constants/invoiceConstants";
import { formatPrice, formatDate } from "../../../utils/formatters";
import { useApiCall } from "../../../hooks/useApiCall";

const { Content } = Layout;
const { Title, Text } = Typography;

const CustomerInvoicesPage = () => {
  const [invoices, setInvoices] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [detailInvoice, setDetailInvoice] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const pageSize = 10;

  const customerId = localStorage.getItem("userId");
  const { loading, execute } = useApiCall();

  const fetchInvoices = async (page = currentPage) => {
    try {
      const data = await execute(() =>
        getInvoicesByCustomer(customerId, page - 1, pageSize)
      );
      setInvoices(data.content || []);
      setTotalElements(data.totalElements || 0);
    } catch (error) {
      console.error("Error fetching invoices:", error);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, [currentPage]);

  const handleDownloadPdf = async (invoice) => {
    try {
      const response = await downloadInvoicePdf(invoice.id);
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `fatura-${invoice.invoiceNumber}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success("PDF indirildi");
    } catch (error) {
      console.error("Error downloading PDF:", error);
    }
  };

  const handleViewDetail = async (invoice) => {
    try {
      setDetailLoading(true);
      setShowDetail(true);
      const response = await getInvoiceById(invoice.id);
      setDetailInvoice(response.data);
    } catch (error) {
      console.error("Error fetching invoice detail:", error);
    } finally {
      setDetailLoading(false);
    }
  };

  const columns = [
    {
      title: "Fatura No",
      dataIndex: "invoiceNumber",
      key: "invoiceNumber",
    },
    {
      title: "Toplam",
      dataIndex: "totalAmount",
      key: "totalAmount",
      render: (val) => <Text strong>{formatPrice(val)}</Text>,
      align: "right",
    },
    {
      title: "Durum",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const { color, text } = getInvoiceStatusInfo(status);
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: "Fatura Tarihi",
      dataIndex: "invoiceDate",
      key: "invoiceDate",
      render: (date) => formatDate(date),
    },
    {
      title: "Vade Tarihi",
      dataIndex: "dueDate",
      key: "dueDate",
      render: (date) => formatDate(date),
    },
    {
      title: "İşlemler",
      key: "actions",
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Detay">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetail(record)}
            />
          </Tooltip>
          <Tooltip title="PDF İndir">
            <Button
              type="text"
              size="small"
              icon={<FilePdfOutlined />}
              onClick={() => handleDownloadPdf(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <Layout style={{ minHeight: "100vh", background: "#f0f2f5" }}>
      <Content style={{ padding: "24px" }}>
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Title level={2} style={{ marginBottom: 24 }}>
            <FileTextOutlined style={{ color: "#1890ff", marginRight: 12 }} />
            Faturalarım
          </Title>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.4 }}
        >
          <Table
            columns={columns}
            dataSource={invoices}
            rowKey="id"
            loading={loading}
            bordered
            scroll={{ x: true }}
            pagination={{
              current: currentPage,
              pageSize,
              total: totalElements,
              onChange: (page) => setCurrentPage(page),
              showSizeChanger: false,
              showTotal: (total) => `Toplam ${total} fatura`,
            }}
          />
        </motion.div>

        {showDetail && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            style={{ marginTop: 24 }}
          >
            <Spin spinning={detailLoading}>
              <Row gutter={[16, 16]}>
                <Col span={24}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 16,
                    }}
                  >
                    <Title level={4} style={{ margin: 0 }}>
                      Fatura Detayı{detailInvoice ? ` - ${detailInvoice.invoiceNumber}` : ""}
                    </Title>
                    <Button onClick={() => { setShowDetail(false); setDetailInvoice(null); }}>Kapat</Button>
                  </div>
                </Col>
                {detailInvoice && (
                  <Col span={24}>
                    <Table
                      columns={[
                        { title: "Açıklama", dataIndex: "description", key: "description" },
                        { title: "Miktar", dataIndex: "quantity", key: "quantity", align: "center" },
                        {
                          title: "Birim Fiyat",
                          dataIndex: "unitPrice",
                          key: "unitPrice",
                          render: (val) => formatPrice(val),
                          align: "right",
                        },
                        {
                          title: "KDV",
                          dataIndex: "taxAmount",
                          key: "taxAmount",
                          render: (val) => formatPrice(val),
                          align: "right",
                        },
                        {
                          title: "Toplam",
                          dataIndex: "totalPrice",
                          key: "totalPrice",
                          render: (val) => <Text strong>{formatPrice(val)}</Text>,
                          align: "right",
                        },
                      ]}
                      dataSource={detailInvoice.items || []}
                      rowKey="id"
                      pagination={false}
                      bordered
                      scroll={{ x: true }}
                    />
                    <Row justify="end" style={{ marginTop: 16 }}>
                      <Col>
                        <Space direction="vertical" size={4} style={{ textAlign: "right" }}>
                          <Text>Ara Toplam: {formatPrice(detailInvoice.subtotal)}</Text>
                          <Text>KDV: {formatPrice(detailInvoice.taxAmount)}</Text>
                          <Title level={4} style={{ margin: 0 }}>
                            Genel Toplam: {formatPrice(detailInvoice.totalAmount)}
                          </Title>
                        </Space>
                      </Col>
                    </Row>
                  </Col>
                )}
              </Row>
            </Spin>
          </motion.div>
        )}
      </Content>
    </Layout>
  );
};

export default CustomerInvoicesPage;
